import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AttendanceRecordService } from 'app/services/attendance-record-service';
import { combineLatest, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { AttendanceRecordState, SchoolActivityInstance } from '../../../models';
import { BulkSignUpForActivityReq, SchoolActivityAttendee, SchoolActivityService } from '../../../services/school-activity.service';
import { ToastService } from '../../../services/toast.service';
import {
	BulkRemoveAttendeeAndSignUpAction,
	BulkRemoveAttendeeAndSignUpActionFailure,
	BulkSignOutForActivityAction,
	BulkSignOutForActivityFailureAction,
	BulkSignOutForActivitySuccessAction,
	BulkSignUpForActivityAction,
	BulkSignUpForActivityFailureAction,
	BulkSignUpForActivitySuccessAction,
	RemoveAttendeeAndSignUpAction,
	RemoveAttendeeAndSignUpActionFailure,
	RemoveAttendeeAndSignUpActionSuccess,
	SignUpForActivityAction,
	SignUpForActivityFailureAction,
	SignUpForActivitySuccessAction,
} from '../actions/school-activity-attendees.actions';

@Injectable()
export class SchoolActivityAttendeesEffects {
	SignUpForActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(SignUpForActivityAction),
			exhaustMap(({ req, instance }) => {
				return this.schoolActivityService.StudentSignUpForActivityHTTP(req).pipe(
					map((attendee) => {
						this.toastService.openToast({ title: 'Signed up for activity!', type: 'success' });
						return SignUpForActivitySuccessAction({ attendee: attendee });
					}),
					catchError((error) => of(SignUpForActivityFailureAction({ error: error.message })))
				);
			})
		);
	});

	BulkSignUpForActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(BulkSignUpForActivityAction),
			exhaustMap(({ req, instance, checkIn }: { req: BulkSignUpForActivityReq; instance: SchoolActivityInstance; checkIn: boolean }) => {
				return this.schoolActivityService.TeacherBulkSignUpForActivityHTTP(req, instance).pipe(
					switchMap((attendees: SchoolActivityAttendee[]) => {
						const records = this.attendanceRecordService.getRecordsForInstanceId(instance.id);
						return combineLatest([of(attendees), records]);
					}),
					tap(([_attendees, attendance]) => {
						if (checkIn) {
							attendance
								.filter((record) => req.user_ids.includes(record.attendance_user.id))
								.map((record) => void this.attendanceRecordService.checkIn(record.id, AttendanceRecordState.Present, instance));
						}
					}),
					map(([attendees, _attendance]) => {
						return BulkSignUpForActivitySuccessAction({ attendees: attendees });
					}),
					catchError((error) => of(BulkSignUpForActivityFailureAction({ error: error.message })))
				);
			})
		);
	});

	BulkSignOutForActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(BulkSignOutForActivityAction),
			exhaustMap(({ attendeeIds }) => {
				return this.schoolActivityService.TeacherBulkSignOutForActivityHTTP(attendeeIds).pipe(
					map((_attendees) => BulkSignOutForActivitySuccessAction({ attendeeIds: attendeeIds })),
					catchError((error) => of(BulkSignOutForActivityFailureAction({ error: error.message })))
				);
			})
		);
	});

	RemoveAttendeeAndSignUpForActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(RemoveAttendeeAndSignUpAction),
			exhaustMap(({ attendeeId, req, instance }) => {
				return this.schoolActivityService.RemoveAttendeeFromActivityInstanceHTTP(attendeeId).pipe(
					map((_attendee) => {
						if (req && instance) {
							return SignUpForActivityAction({ req, instance });
						}
						this.toastService.openToast({ title: `Student removed`, type: 'success' });
						return RemoveAttendeeAndSignUpActionSuccess({ attendeeId });
					}),
					catchError((error) => {
						this.toastService.openToast({ title: `Error while removing student, try again`, type: 'error' });
						return of(RemoveAttendeeAndSignUpActionFailure({ error: error.message }));
					})
				);
			})
		);
	});

	BulkRemoveAttendeeAndSignUpForActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(BulkRemoveAttendeeAndSignUpAction),
			exhaustMap(({ attendeeIds, req, instance, checkIn }) => {
				if (!attendeeIds.length) {
					return of(BulkSignUpForActivityAction({ req, instance, checkIn }));
				}

				return this.schoolActivityService.TeacherBulkSignOutForActivityHTTP(attendeeIds).pipe(
					map((_attendees) => {
						return BulkSignUpForActivityAction({ req, instance, checkIn });
					}),
					catchError((error) => {
						this.toastService.openToast({ title: `Error while removing students, try again`, type: 'error' });
						return of(BulkRemoveAttendeeAndSignUpActionFailure({ error: error.message }));
					})
				);
			})
		);
	});

	constructor(
		private actions$: Actions,
		private schoolActivityService: SchoolActivityService,
		private toastService: ToastService,
		private attendanceRecordService: AttendanceRecordService
	) {}
}
